import { defineStore } from 'pinia';
import { ref, watch } from 'vue';

import * as AuthAPI from '@/apis/auth';
import { isDatadogSyntheticsBrowser } from '@/config';
import { env } from '@/env';
import { useIdentityStore } from '@/stores/identity';
import { loadScript } from '@/utils/dom';
import { logger } from '@/utils/logger';

export const useHubspotStore = defineStore('hubspot', () => {
  const identityStore = useIdentityStore();
  const ready = ref(false);
  const ctaReady = ref(false);
  const isOpen = ref(false);
  const loadingWidget = ref(false);
  const showConversation = ref(false);
  const lastIdentifiedEmail = ref(null);
  let initGeneration = 0;

  const enabled = env.hubspotEnabled && !isDatadogSyntheticsBrowser && !window.Cypress;

  function onWidgetLoaded() {
    loadingWidget.value = false;
  }

  function onWidgetClosed() {
    isOpen.value = false;
  }

  function onConversationsAPIReady() {
    ready.value = true;
    // Register listeners once. Re-binding on every initWidget() would accumulate
    // handlers across impersonation switches.
    if (window.HubSpotConversations) {
      window.HubSpotConversations.on('widgetLoaded', onWidgetLoaded);
      window.HubSpotConversations.on('widgetClosed', onWidgetClosed);
    }
  }

  function onCallToActionAPIReady() {
    ctaReady.value = true;
  }

  function boot() {
    // Always init the tracking queue. trackEvent() runs from router.afterEach
    // hooks even when HubSpot itself is disabled (Cypress, synthetics), so
    // _hsq must always be defined. Idempotent — the hs-scripts.com loader
    // replaces _hsq with its own tracker object once ready.
    window._hsq = window._hsq || [];

    if (enabled) {
      window.hsConversationsSettings = {
        loadImmediately: false,
        inlineEmbedSelector: '#dh-hubspot-chat',
      };

      if (window.HubSpotConversations) {
        onConversationsAPIReady();
      } else {
        window.hsConversationsOnReady = [onConversationsAPIReady];
      }

      if (window.HubSpotCallsToActions) {
        onCallToActionAPIReady();
      } else {
        window.hsCallsToActionsReady = [onCallToActionAPIReady];
      }

      loadScript(`//js-na1.hs-scripts.com/${env.hubspotTrackingCode}.js`, {
        id: 'hs-script-loader',
      });
    }
  }

  async function getToken() {
    try {
      const res = await AuthAPI.getHubspotSupportToken();
      return res.data.token;
    } catch {
      return '';
    }
  }

  function loadWidget() {
    const status = window.HubSpotConversations.widget.status();
    if (status.loaded) {
      window.HubSpotConversations.widget.refresh();
    } else {
      window.HubSpotConversations.widget.load();
      loadingWidget.value = true;
    }
  }

  function teardown() {
    initGeneration += 1;
    if (window.hsConversationsSettings) {
      delete window.hsConversationsSettings.identificationEmail;
      delete window.hsConversationsSettings.identificationToken;
    }
    const widget = window.HubSpotConversations?.widget;
    if (widget?.status?.().loaded) {
      window.HubSpotConversations.clear({ resetWidget: true });
      widget.remove();
    }
    loadingWidget.value = false;
    isOpen.value = false;
    showConversation.value = false;
    lastIdentifiedEmail.value = null;
  }

  async function initWidget() {
    const identity = identityStore.identity;
    if (!identity) {
      teardown();
      return;
    }

    const email = identity.email;
    const gen = ++initGeneration;

    const token = await getToken();
    if (gen !== initGeneration) return;
    if (!token) {
      // Don't tear down a working session over a transient token failure. The
      // tradeoff: agent will see "anonymous visitor" if this fails on first init.
      logger.warn('[hubspot] visitor token unavailable; skipping widget identity update');
      return;
    }

    window.hsConversationsSettings.identificationEmail = email;
    window.hsConversationsSettings.identificationToken = token;
    window._hsq.push(['identify', { email }]);

    const widget = window.HubSpotConversations.widget;
    const status = widget.status();

    // Same visitor as last time → just re-render. Avoids wiping welcome state and
    // unread badges for returning customers.
    if (status.loaded && lastIdentifiedEmail.value === email) {
      widget.refresh();
      return;
    }

    // Real identity change (impersonation start/end) or first load: clear the old
    // visitor's messagesUtk cookie and recreate the widget instance under the new
    // identification token. widget.refresh() does NOT re-identify; widget.load() does.
    if (status.loaded) {
      window.HubSpotConversations.clear({ resetWidget: true });
    }
    loadingWidget.value = true;
    widget.load();
    lastIdentifiedEmail.value = email;
  }

  function show() {
    loadWidget();
    window.HubSpotConversations.widget.open();
    isOpen.value = true;
    showConversation.value = true;
  }

  function toggle() {
    loadWidget();

    if (isOpen.value) {
      window.HubSpotConversations.widget.close();
    } else {
      window.HubSpotConversations.widget.open();
      isOpen.value = true;
    }
    showConversation.value = !showConversation.value;
  }

  function refreshCallToAction() {
    if (ctaReady.value) {
      window.HubSpotCallsToActions.refresh();
    }
  }

  function trackEvent({ eventName, eventDetails }) {
    if (!window._hsq) return;
    window._hsq.push([eventName], eventDetails);
  }

  watch(
    () => ctaReady.value,
    () => {
      refreshCallToAction();
    },
    { immediate: true },
  );

  const chatTargetReady = ref(false);

  function chatMounted() {
    chatTargetReady.value = true;
  }

  function chatUnmounted() {
    chatTargetReady.value = false;
  }

  watch(
    () => ({
      identity: identityStore.identity,
      ready: ready.value,
      mounted: chatTargetReady.value,
    }),
    (context) => {
      if (enabled && context.ready && context.mounted) {
        if (context.identity) {
          void initWidget();
        } else {
          teardown();
        }
      }
    },
    { immediate: true },
  );

  return {
    loadingWidget,
    enabled,
    ready,
    showConversation,
    boot,
    show,
    toggle,
    refreshCallToAction,
    trackEvent,
    chatMounted,
    chatUnmounted,
    teardown,
  };
});
